import React from 'react';
import { clsx } from 'clsx';
import type { Control, UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import type { CuppingFormData } from '../../../hooks/inventory-hooks/useCuppingProfileForm';
import { ScoreSlider, NotesField } from './CuppingFormFields';
import { useLanguage } from '../../../features/language/useLanguage';

export const SimplifiedSection: React.FC<{
  control: Control<CuppingFormData>;
  register: UseFormRegister<CuppingFormData>;
  setValue: UseFormSetValue<CuppingFormData>;
  watch: UseFormWatch<CuppingFormData>;
  readOnly?: boolean;
  enableSectionLocks?: boolean;
  isSectionLocked?: (key: string) => boolean;
  renderSectionLock?: (key: string, className?: string) => React.ReactNode;
}> = ({ control, register, setValue, watch, readOnly, enableSectionLocks, isSectionLocked, renderSectionLock }) => {
  const { t } = useLanguage();
  const blocks: Array<{ name: keyof CuppingFormData; label: string; notes: keyof CuppingFormData; testId: string }> = [
    { name: 'fragrance_aroma', label: t('cupping.sca.fragrance') + '/Aroma', notes: 'notes_fragrance', testId: 'simp-frag' },
    { name: 'flavor', label: t('cupping.formSections.labels.flavor'), notes: 'notes_flavor', testId: 'simp-flavor' },
    { name: 'acidity', label: t('cupping.formSections.labels.acidity'), notes: 'notes_acidity', testId: 'simp-acidity' },
    { name: 'body', label: t('recipes.tasting.attributes.Body'), notes: 'notes_body', testId: 'simp-body' },
    { name: 'aftertaste', label: t('cupping.formSections.labels.aftertaste'), notes: 'notes_mouthfeel', testId: 'simp-aftertaste' },
    { name: 'sweetness', label: t('cupping.formSections.labels.sweetness'), notes: 'notes_sweetness', testId: 'simp-sweetness' },
  ];

  const acidityAttributeOptions = [
    t('cupping.simplified.dryAcidity'),
    t('cupping.simplified.sweetAcidity'),
  ];

  return (
    <div className="space-y-px" data-test-id="cupping-simplified-section">
      {blocks.map((b) => {
        const sectionKey =
          b.name === 'fragrance_aroma'
            ? 'simp-fragrance-aroma'
            : b.name === 'flavor'
              ? 'simp-flavor'
              : b.name === 'acidity'
                ? 'simp-acidity'
                : b.name === 'body'
                  ? 'simp-body'
                  : b.name === 'aftertaste'
                    ? 'simp-aftertaste'
                    : 'simp-sweetness';
        const sectionReadOnly = !!readOnly || (!!enableSectionLocks && (isSectionLocked?.(sectionKey) ?? true));
        return (
          <div key={String(b.name)} className="grid grid-cols-12 border border-gray-300 bg-white relative">
            <div className="col-span-8 p-4 border-r border-gray-300">
              <ScoreSlider control={control} name={b.name} label={b.label} max={5} setValue={setValue} disabled={sectionReadOnly} />
              {b.name === 'acidity' && (
                <div className="mt-4">
                  <div className="flex flex-col gap-2">
                    {acidityAttributeOptions.map(option => {
                      const selected = watch('acidity_attributes') || [];
                      const isChecked = selected.includes(option);
                      const isMaxReached = selected.length >= 1;
                      const isDisabled = sectionReadOnly || (!isChecked && isMaxReached);
                      const id = `simp-acidity-attr-${option.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`;

                      return (
                        <label
                          key={option}
                          htmlFor={id}
                          className={clsx(
                            'flex items-center gap-2 text-xs text-gray-700',
                            isDisabled ? 'cursor-not-allowed opacity-80' : 'cursor-pointer'
                          )}
                        >
                          <input
                            id={id}
                            type="checkbox"
                            value={option}
                            disabled={isDisabled}
                            {...register('acidity_attributes')}
                            className={clsx(
                              'h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500',
                              isDisabled && 'cursor-not-allowed bg-gray-100'
                            )}
                          />
                          <span>{option}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <div className="col-span-4 p-4">
              <NotesField register={register} name={b.notes} label={t('reports.details.notes')} disabled={sectionReadOnly} />
            </div>
            {renderSectionLock?.(sectionKey, 'absolute top-3 right-3')}
          </div>
        );
      })}

      <div className="grid grid-cols-12 border border-gray-300 bg-white relative">
        <div className="col-span-12 p-4">
          <ScoreSlider
            control={control}
            name="balance"
            label={t('cupping.formSections.labels.balance')}
            max={3}
            setValue={setValue}
            disabled={!!readOnly || (!!enableSectionLocks && (isSectionLocked?.('simp-balance') ?? true))}
            minText={t('cupping.simplified.notBalance')}
            midText={t('cupping.formSections.labels.balance')}
            maxText={t('cupping.simplified.wellBalance')}
          />
        </div>
        {renderSectionLock?.('simp-balance', 'absolute top-3 right-3')}
      </div>

      <div className="grid grid-cols-12 border border-gray-300 bg-white relative">
        <div className="col-span-12 p-4">
          <div className="text-sm font-bold text-gray-800 mb-2">{t('cupping.simplified.roastDefects')}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
            <ScoreSlider
              control={control}
              name="roast_defect_underdevelopment"
              label={t('cupping.simplified.underdevelopment')}
              max={5}
              setValue={setValue}
              disabled={!!readOnly || (!!enableSectionLocks && (isSectionLocked?.('simp-roast-defects') ?? true))}
            />
            <ScoreSlider
              control={control}
              name="roast_defect_overdevelopment"
              label={t('cupping.simplified.overdevelopment')}
              max={5}
              setValue={setValue}
              disabled={!!readOnly || (!!enableSectionLocks && (isSectionLocked?.('simp-roast-defects') ?? true))}
            />
            <ScoreSlider
              control={control}
              name="roast_defect_baked"
              label={t('cupping.simplified.baked')}
              max={5}
              setValue={setValue}
              disabled={!!readOnly || (!!enableSectionLocks && (isSectionLocked?.('simp-roast-defects') ?? true))}
            />
            <ScoreSlider
              control={control}
              name="roast_defect_scorched"
              label={t('cupping.simplified.scorched')}
              max={5}
              setValue={setValue}
              disabled={!!readOnly || (!!enableSectionLocks && (isSectionLocked?.('simp-roast-defects') ?? true))}
            />
          </div>
        </div>
        {renderSectionLock?.('simp-roast-defects', 'absolute top-3 right-3')}
      </div>
    </div>
  );
};
